import React from "react";
import { useDispatch } from "react-redux";

// Actions
import { updateCampaignMediaDuration } from "../../../actions/campaign-media/CampaignMediaActions";

// Constants and Utils
import { getModifiedPopulation } from "../../../utils/ReachAndFrequencyUtils";

// Urls
import { RedirectTo } from "../../../urls/PageLinksURL";

// Components
import {
  InfoCell,
  ImpressionsCell,
  LtsCell,
  FrequencyCell,
  ReachCell,
} from "../../../components/campaign-media-table-row/MediaRow";
import { DurationCell } from "../../../components/campaign-table-row/CampaignRowCells";
import TableHeaders from "../../../components/table/TableHeaders";
import Pagination from "../../../components/pagination/Pagination";
import { OtsAndReachHeader } from "../../../components/table/OtsAndReachHeader";
import NoTableDataMessage from "../../../components/table/NoTableDataMessage";

// Page Constants
const otsHeaderInfo = [
  {
    displayText: "TG Impressions",
    className: "bg-light-green",
    percentage: "25",
  },
  {
    displayText: "Total Impressions",
    className: "bg-light-blue",
    percentage: "100",
  },
];

const reachHeaderInfo = [
  {
    displayText: "TG Reach",
    className: "bg-light-pink",
    percentage: "25",
  },
  {
    displayText: "Total Reach",
    className: "bg-light-yellow",
    percentage: "100",
  },
];

const citySummaryTableHeaders = (cityPopulation) => [
  {
    title: {
      displayName: "Site Name",
      className: "col-3 align-middle",
    },
    subTitle: {
      displayName: "Media type, Dimensions, Lighting, Region",
      className: "sub-text",
    },
  },

  {
    title: {
      displayName: "Start and End Date",
      className: "col-2 align-middle text-right",
    },
    subTitle: {
      displayName: "Duration",
      className: "sub-text",
    },
  },

  {
    element: <OtsAndReachHeader headerInfo={otsHeaderInfo} />,
  },
  {
    element: (
      <OtsAndReachHeader
        headerInfo={reachHeaderInfo}
        subText={`Population: ${getModifiedPopulation(cityPopulation)}`}
      />
    ),
  },

  {
    title: {
      displayName: "Frequency",
      className: "col-1 text-right align-middle",
    },
  },

  {
    title: {
      displayName: "LTS",
      className: "col-1 text-center align-middle",
    },
  },
];

function MediaRowForCitySummary({ media = {}, cityPopulation }) {
  const dispatch = useDispatch();

  const { endTimestamp, startTimestamp, campaignId, id: mediaId } = media;

  // function to update date
  function onDateSelect(selectedDate) {
    dispatch(updateCampaignMediaDuration(selectedDate, media));
  }

  const dateObj = { startTimestamp, endTimestamp };

  // Redirect Url
  const campaignMediaViewPageUrl = RedirectTo.campaignMediaViewPageUrl
    .replace(":campaignId", campaignId)
    .replace(":mediaId", mediaId);

  return (
    <tr>
      <InfoCell
        mediaElementStyle={"p-2"}
        media={media}
        redirectUrl={campaignMediaViewPageUrl}
      />

      {endTimestamp && startTimestamp ? (
        <DurationCell
          durationElementStyle={"py-2 px-0 align-middle text-right"}
          onDateSelect={onDateSelect}
          duration={media.duration}
          dateObj={dateObj}
        />
      ) : (
        <td className="p-2 align-middle text-right">--</td>
      )}

      <ImpressionsCell impressionsElementStyle="align-middle" media={media} />

      <ReachCell
        reachElementStyle="align-middle"
        media={media}
        cityPopulation={cityPopulation}
        showPopulation={false}
      />
      <FrequencyCell
        frequencyElementClass={"p-2 text-right align-middle"}
        reachFrequency={media.reachFrequency}
      />

      <LtsCell ltsElementClass={"p-2 text-center align-middle"} media={media} />
    </tr>
  );
}

function CampaignCityViewMediaTable({
  mediaList,
  pagination,
  loadCityMedia,
  cityPopulation,
}) {
  const { totalCount, pageNo, pageSize } = pagination;

  const isMediaSitesPresent = mediaList.length > 0;

  return (
    <div className="table-responsive mt-4">
      <table className="table table-media">
        {/* Table header */}
        <TableHeaders
          tableHeaders={citySummaryTableHeaders(cityPopulation)}
          headerClass={"thead"}
        />

        <tbody>
          {!isMediaSitesPresent && (
            <NoTableDataMessage
              colSpan={6}
              message="City don't have any media sites"
            />
          )}

          {/* Table rows */}
          {isMediaSitesPresent &&
            mediaList.map((media) => (
              <MediaRowForCitySummary
                media={media}
                key={media.id}
                cityPopulation={cityPopulation}
              />
            ))}
        </tbody>
      </table>

      {/* Table Pagination */}
      <Pagination
        totalCount={totalCount}
        pageNo={pageNo}
        pageSize={pageSize}
        onClick={loadCityMedia}
      />
    </div>
  );
}

export default CampaignCityViewMediaTable;
